import { getShowStudyPackViews } from "@helpers/exams";

/* ----------------- Helpers --------------- */
import { getNumAttempted, groupTagsByType } from "@helpers/content/tag";

/* ----------------- Hooks --------------- */
import { useTagsStore } from "./useTagsStore";

import { isScreenshotMode } from "@constants/common";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { getColorScaleAndDescription } from "@helpers/content/metadata";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import { formatNumber } from "@memorang/helpers/src/common";
import type { MixedItemDist, MixedItemTag } from "@memorang/types/src/tag";
/* ----------------- Router --------------- */
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";

const getMasteryKeyTopics = (
	mixedItemDist: MixedItemDist,
	hasAccess?: boolean,
	isMobile?: boolean,
	isDetailedList?: boolean,
) => {
	const getTagsToDisplay = (tags: MixedItemTag[]) => {
		if (isMobile && !isDetailedList) {
			const freeTags = tags.filter((tag) => !tag.locked);
			const premiumTags = tags.filter((tag) => tag.locked);
			return freeTags?.concat(premiumTags);
		}
		return tags.sort((a, b) => Number(a.locked) - Number(b.locked));
	};
	const { tags, freeTagIds } = mixedItemDist;
	const groupedTagsByType = groupTagsByType(
		tags,
		"Display",
		freeTagIds,
		hasAccess,
	);
	const allTags = Object.keys(groupedTagsByType).flatMap(
		(key) => groupedTagsByType[key],
	);
	const tagsToDisplay = getTagsToDisplay(allTags);
	return tagsToDisplay;
};
export const useMasteryKeyTopicsSectionData = (
	mixedItemDist?: MixedItemDist,
	hasAccess?: boolean,
) => {
	const router = useRouter();

	const masterySubheaderLabel = useAppStore(
		(state) => state.homeView?.masterySubheaderLabel,
	);

	const theme = useAppTheme();
	const { t } = useTranslation();
	const showPointsBasedProgress = useAppStore(
		(state) => state.reportView?.formative?.showPointsBasedScore,
	);

	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();

	const setCurrentSelectedMasteryItems = useTagsStore(
		(state) => state.setCurrentSelectedMasteryItems,
	);
	const { isMobile } = useDevice();
	const section = {
		data: [],
		type: "progress",
		onPress: undefined,
	};

	const isStudyPackEnabled = getShowStudyPackViews(studyPackCollectionId);
	if (!isStudyPackEnabled) {
		return null;
	}

	if (!mixedItemDist) {
		return {
			...section,
			sectionTitle: masterySubheaderLabel || t("dashboard.skills.title"),
			loading: true,
		};
	}

	const { tags } = mixedItemDist;

	const tagType = tags.find((tag) =>
		tag.displayType?.includes("Display"),
	)?.type;

	const numTotalItems = mixedItemDist?.contents?.reduce(
		(acc, tag) => acc + tag.totalCount,
		0,
	);
	const masteryTopics = getMasteryKeyTopics(mixedItemDist, hasAccess, isMobile);
	const numTotalTagTypes = masteryTopics.length;

	const mappedMasteryTopics = masteryTopics.map((item) => {
		return {
			...item,
			locked: isStudyPackEnabled ? item.locked : false,
			score: showPointsBasedProgress
				? getNumAttempted(item.distribution)
				: isScreenshotMode
					? Math.floor(Math.random() * (item.numItems - 8)) + 9
					: item.numCorrect,
			...getColorScaleAndDescription({
				theme,
				tag: item,
				t,
				includeColorScale: true,
			}),
		};
	});

	const lockedTags = mappedMasteryTopics.filter((tag) => tag.locked);
	return {
		...section,
		sectionTitle: `${numTotalTagTypes} ${masterySubheaderLabel || t("dashboard.skills.title")} • ${formatNumber(numTotalItems)} items`,
		loading: false,
		data: mappedMasteryTopics,
		chipProps:
			hasAccess || !lockedTags.length
				? undefined
				: {
						label: t("premium.chip"),
						icon: "crown-outline" as const,
						background: theme.palette.chips.premium,
					},
		onPress: () => {
			const items = getMasteryKeyTopics(
				mixedItemDist,
				hasAccess,
				isMobile,
				true,
			);

			setCurrentSelectedMasteryItems(items);
			router.push({
				pathname: "/progress/detailed-list/mastery",
				params: {
					type: tagType || "Topic",
				},
			});
		},
	};
};
